import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import { compile, match } from 'path-to-regexp'
import { PropsWithChildren } from 'react'
import { PageParams, PreviewData } from '../@types/infrastructur'
import AvailablePages from '../graphql/AvailablePages.graphql'
import BlueprintByUri from '../graphql/BlueprintByUri.graphql'
import blueprintFinder from '../lib/blueprintFinder'
import genericPageFinder from '../lib/genericPageFinder'
import { request, statamicConfig } from '../StatamicGraphQLClient'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import flchDynamicPageFinder from '@/src/components/flch/navigation/flchDynamicPageFinder'
import { reportRedirect } from '@/src/StatamicApiClient'

export async function getStaticProps(
  context: GetStaticPropsContext<PageParams, PreviewData>
): Promise<GetStaticPropsResult<any>> {
  const urlParts = [...(context.params?.uri || [])]
  const currentUri = `/${urlParts.join('/')}`
  const { entry } = await request<{
    entry?: { blueprint: string; published: boolean }
  }>(
    BlueprintByUri,
    {
      uri: currentUri,
      locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
      collections: statamicConfig.collections,
    },
    context.previewData?.statamicToken
  )

  if (
    entry &&
    (entry.published ||
      (!entry.published && context.previewData?.statamicToken)) &&
    entry.blueprint !== 'flch_dynamic'
  ) {
    const handler = blueprintFinder(entry.blueprint)

    if (!handler) {
      return {
        notFound: true,
      }
    }

    const staticProps = await handler.getStaticProps(context)

    // if there are no props its a redirect or page not found
    if ('props' in staticProps) {
      staticProps.props.blueprint = entry.blueprint
    }

    return {
      revalidate: 60 * 60,
      ...staticProps,
    }
  }

  if (process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE === 'blink') {
    const adminSiteUrl = `${
      process.env.NEXT_PUBLIC_BLINK_ADMIN_URL
    }/api/website/v2/generic-pages/${context.params?.uri?.join('/') || ''}`

    const adminSite = await fetch(adminSiteUrl)

    if (adminSite.ok) {
      const values = await adminSite.json()
      const handler = genericPageFinder(values.type)

      if (!handler) {
        return {
          notFound: true,
        }
      }

      const staticProps = await handler.getStaticProps(context, values)

      return {
        props: {
          generic: true,
          ...values,
          // @ts-ignore
          ...staticProps.props,
        },
      }
    }
  } else if (process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE === 'flch') {
    const adminSiteUrl = `${
      process.env.NEXT_PUBLIC_BLINK_ADMIN_URL
    }/api/fahrlehrer-ch/pages/${context.params?.uri?.join('/') || ''}`

    const adminSite = await fetch(adminSiteUrl)

    if (adminSite.ok) {
      const page: FahrlehrerCh.Page = await adminSite.json()
      const handler = flchDynamicPageFinder(page.type)

      if (!handler) {
        return {
          notFound: true,
        }
      }

      const staticProps = await handler.getStaticProps(context, page.data)

      return {
        revalidate: 60 * 60,
        props: {
          dynamicFlch: true,
          type: page.type,
          // @ts-ignore
          ...staticProps.props,
        },
      }
    }
  }

  try {
    const redirectsResponse = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/redirects?site=${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}`
    )
    const redirects: {
      source: string
      destination: string
      match_type: 'exact'
      type: number
    }[] = await redirectsResponse.json()

    for (const redirect of redirects) {
      try {
        const matchFn = match(redirect.source, { decode: decodeURIComponent })
        const matched = matchFn(`/${context.params?.uri?.join('/') || ''}`)

        if (matched) {
          const toPath = compile(redirect.destination, {
            encode: encodeURIComponent,
          })
          const finalDestination = toPath(matched.params)

          if (process.env.NODE_ENV === 'production') {
            await reportRedirect(
              `/${context.params?.uri?.join('/') || ''}`,
              finalDestination,
              process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE
            )
          }

          return {
            redirect: {
              destination: finalDestination,
              statusCode: redirect.type as 301 | 302,
            },
          }
        }
      } catch (_) {}
    }
  } catch (_) {}

  return {
    notFound: true,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<PageParams>
> {
  const {
    entries: { data: pages },
  } = await request<{
    entries: { data: { blueprint: string; uri: string }[] }
  }>(AvailablePages, {
    locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
    collections: statamicConfig.collections,
  })

  const paths = pages
    .filter((entry) => {
      return blueprintFinder(entry.blueprint) !== undefined
    })
    .map((entry) => {
      return {
        params: {
          uri: entry.uri.split('/').splice(1),
        },
      }
    })

  if (process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE === 'flch') {
    try {
      const dynamicPagesResponse = await fetch(
        `${process.env.NEXT_PUBLIC_BLINK_ADMIN_URL}/api/fahrlehrer-ch/dynamic-pages`
      )

      if (dynamicPagesResponse.ok) {
        const dynamicPaths: string[] = await dynamicPagesResponse.json()

        for (const path of dynamicPaths) {
          paths.push({
            params: {
              uri: path.split('/').filter(Boolean),
            },
          })
        }
      }
    } catch (_) {}
  }

  return {
    paths,
    fallback: 'blocking',
  }
}

export default function Page(params: PropsWithChildren<any>) {
  const Component = params.generic
    ? genericPageFinder(params.type)?.default
    : params.dynamicFlch
      ? flchDynamicPageFinder(params.type)?.default
      : blueprintFinder(params.blueprint)?.default

  if (!Component) {
    return null
  }

  return <Component {...params} />
}
